import React, { useState, useEffect } from 'react';
import { Star, X, MessageSquare, Send, Check } from 'lucide-react';
import { authFetch, readJSON } from '../lib/auth';

const FEEDBACK_DONE_KEY = 'fls_feedback_submitted_v1';

/**
 * FeedbackWidget — combines:
 *   1. A persistent floating button (bottom-left of the studio) that opens the modal
 *   2. A controlled modal that can be auto-opened by parent (post-export prompt)
 *
 * Submits to POST /api/feedback. Works for both signed-in and anonymous users.
 */
export default function FeedbackWidget({ open: controlledOpen, onOpenChange, context = 'manual' }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = typeof controlledOpen === 'boolean' ? controlledOpen : openInternal;

  const setOpen = (val) => {
    if (typeof controlledOpen === 'boolean') {
      onOpenChange?.(val);
    } else {
      setOpenInternal(val);
    }
  };

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Reset state every time the modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setRating(0); setHover(0); setComment(''); setBusy(false); setDone(false); setError('');
      }, 200);
    }
  }, [open]);

  const submit = async () => {
    if (rating === 0) {
      setError('Please pick at least one star.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await authFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          rating,
          comment: comment.trim() || null,
          context,
        }),
      });
      if (!res.ok) {
        const err = await readJSON(res);
        throw new Error(err?.detail || 'Could not submit feedback');
      }
      setDone(true);
      try { localStorage.setItem(FEEDBACK_DONE_KEY, '1'); } catch (_) {}
      setTimeout(() => setOpen(false), 1500);
    } catch (e) {
      setError(typeof e.message === 'string' ? e.message : 'Could not submit feedback');
    } finally {
      setBusy(false);
    }
  };

  const labels = ['', 'Poor', 'Meh', 'OK', 'Great', 'Amazing'];
  const activeStars = hover || rating;

  return (
    <>
      {/* Floating button — only render when modal not open */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="feedback-floating-btn"
          title="Send feedback"
          className="fixed bottom-28 left-4 z-40 flex items-center gap-1.5 bg-slate-800/80 backdrop-blur-md hover:bg-slate-700/90 text-white border border-slate-600/50 shadow-lg rounded-full px-3 py-2 text-xs transition group"
        >
          <MessageSquare className="w-4 h-4 text-amber-400" />
          <span className="hidden sm:inline">Feedback</span>
          <Star className="w-3.5 h-3.5 text-amber-400 group-hover:scale-110 transition" />
        </button>
      )}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="feedback-modal-backdrop"
        >
          <div
            className="bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-modal"
          >
            <button
              onClick={() => !busy && setOpen(false)}
              aria-label="Close"
              data-testid="feedback-close-btn"
              className="absolute top-3 right-3 text-slate-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {!done ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-5 h-5 text-amber-400" />
                  <h2 className="text-lg font-bold text-white">Rate your experience</h2>
                </div>
                <p className="text-xs text-slate-400 mb-5">
                  Your honest take helps me improve the studio. Anonymous is fine.
                </p>

                <div className="flex justify-center gap-2 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(n)}
                      data-testid={`feedback-star-${n}`}
                      className="transition-transform hover:scale-110 active:scale-95 p-1"
                      aria-label={`Rate ${n} of 5`}
                    >
                      <Star
                        className={`w-9 h-9 ${
                          n <= activeStars
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-slate-600 hover:text-amber-400/60'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <div className="text-center text-xs text-slate-400 h-4 mb-4">
                  {activeStars ? labels[activeStars] : ' '}
                </div>

                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid="feedback-comment-input"
                  placeholder="What did you love? What's missing? (optional)"
                  rows={4}
                  maxLength={2000}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 resize-none"
                />

                {error && (
                  <div data-testid="feedback-error" className="text-sm text-rose-400 mt-2">
                    {error}
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={submit}
                    disabled={busy || rating === 0}
                    data-testid="feedback-submit-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <Send className="w-4 h-4" />
                    {busy ? 'Sending…' : 'Send feedback'}
                  </button>
                  <button
                    onClick={() => !busy && setOpen(false)}
                    data-testid="feedback-skip-btn"
                    className="px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm"
                  >
                    Skip
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-6" data-testid="feedback-thanks">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-3">
                  <Check className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Thank you!</h3>
                <p className="text-sm text-slate-400">Your feedback has been recorded.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export const FEEDBACK_LOCAL_KEY = FEEDBACK_DONE_KEY;
